// 007 / 005 slice (P-HOME-1; SA-BAN-6): the Home hero. Shows a school's live banners one at a time
// (1600 × 480, title as the image's accessible name) with Previous / Next and "1 of N"; with no banners,
// the school's notice on a blue tile. Also the school admin's live preview. Live Colour Tiles: a
// square blue tile; changing banner flips the tile (the world's signature move), never on its own.
import { useEffect, useRef, useState } from "react";
import {
  AccessibilityInfo,
  Animated,
  Easing,
  Image,
  Platform,
  StyleSheet,
  View,
} from "react-native";

import { useChromeKind } from "@/shell/chrome/useChromeKind";
import { makeStyles } from "@/theme/makeStyles";
import { useTheme } from "@/theme/useTheme";
import { ChevronIcon } from "@/ui/icons/ChevronIcon";
import { PressableTarget } from "@/ui/PressableTarget";
import { Text } from "@/ui/Text";

import { useBreathing } from "@/ui/motion";

import { DotMatrix, SchoolArt } from "./TileArt";

/** Keeps the last two words together, so a headline never ends on one word alone. */
function noWidow(text: string): string {
  return text.replace(/ (\S+)$/, " $1");
}

export type HeroBanner = { id: string; title: string; imageUrl: string };

const useStyles = makeStyles((t) =>
  StyleSheet.create({
    hero: {
      borderRadius: t.radius.card,
      overflow: "hidden",
      backgroundColor: t.color.tileBlue,
    },
    // Same floor as the skeleton, so a banner replacing it never shifts the page on a phone.
    image: { width: "100%", aspectRatio: 1600 / 480, minHeight: 120 },
    // No live banner: the school's notice on the blue tile, at the banner's proportions.
    fallback: {
      borderRadius: t.radius.card,
      backgroundColor: t.color.tileBlue,
      paddingHorizontal: t.space.lg,
      paddingVertical: t.space.lg,
      aspectRatio: 1600 / 480,
      minHeight: 168,
      justifyContent: "space-between",
      overflow: "hidden",
    },
    // Desktop: missing banners are missing content, so the notice stays a compact tile.
    fallbackWide: { aspectRatio: undefined, minHeight: 0, height: 200 },
    // Phones: an aspect ratio with a height floor would widen the tile past the screen edge
    // (Yoga keeps the ratio by growing the width). The width is the column's; the floor sets the
    // height, and the banner image keeps its own ratio.
    phoneNotice: { aspectRatio: undefined, width: "100%" },
    phoneImage: { minHeight: 0 },
    // A skeleton for a banner: the image's ratio plus the title band under it.
    skeletonBanner: {
      aspectRatio: undefined,
      minHeight: 0,
      width: "100%",
      overflow: "hidden",
    },
    skeletonImage: { width: "100%", aspectRatio: 1600 / 480 },
    skeletonBand: { height: t.size.touchTarget },
    // Desktop board: the notice fills its column beside the 2 x 2 of colour tiles.
    fill: { flex: 1, aspectRatio: undefined, minHeight: 0, height: undefined },
    imageFill: { flex: 1, aspectRatio: undefined },
    fallbackText: { color: t.color.onTile, maxWidth: "70%" },
    headline: { color: t.color.onTile, textTransform: "lowercase" },
    title: {
      flex: 1,
      color: t.color.onTile,
      textTransform: "lowercase",
      paddingHorizontal: t.space.md,
    },
    art: { position: "absolute", right: t.space.sm, bottom: 0 },
    dots: { position: "absolute", right: t.space.md, top: t.space.md },
    skeleton: {
      borderRadius: t.radius.card,
      backgroundColor: t.color.surfaceAlt,
      aspectRatio: 1600 / 480,
      minHeight: 168,
    },
    controls: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      minHeight: t.size.touchTarget,
      backgroundColor: t.color.tileBlue,
    },
    middle: { flexDirection: "row", alignItems: "center", gap: t.space.sm },
    pager: { flexDirection: "row", gap: t.space.xxs },
    pip: {
      width: 8,
      height: 8,
      backgroundColor: t.color.onTile,
      opacity: 0.4,
    },
    pipOn: { width: 20, opacity: 1 },
    position: { color: t.color.onTile },
  }),
);

/**
 * The tile turns over when its content changes (another banner, or a school's banners arriving);
 * the first content after loading appears without a flip, and reduced motion swaps it at once.
 */
function useFlip(key: string | null) {
  // State, not a ref: the value is read while rendering (the interpolations below).
  const [turn] = useState(() => new Animated.Value(0));
  const seen = useRef<string | null>(null);
  useEffect(() => {
    if (key === null) return;
    const previous = seen.current;
    seen.current = key;
    if (previous === null || previous === key) return;
    let cancelled = false;
    void AccessibilityInfo.isReduceMotionEnabled().then((reduce) => {
      if (cancelled || reduce) return;
      turn.setValue(1);
      Animated.timing(turn, {
        toValue: 0,
        duration: 480,
        easing: Easing.out(Easing.exp),
        useNativeDriver: Platform.OS !== "web",
      }).start();
    });
    return () => {
      cancelled = true;
    };
  }, [key, turn]);
  return {
    transform: [
      { perspective: 900 },
      {
        rotateX: turn.interpolate({
          inputRange: [0, 1],
          outputRange: ["0deg", "-80deg"],
        }),
      },
    ],
    opacity: turn.interpolate({ inputRange: [0, 1], outputRange: [1, 0.2] }),
  };
}

export function HeroCarousel({
  banners,
  schoolName,
  fallbackText,
  loading = false,
  expectBanners = true,
  fill = false,
  testID = "home-hero",
}: {
  banners: readonly HeroBanner[];
  schoolName: string;
  fallbackText: string;
  /** True until the first answer: a neutral placeholder, never the "no banners" panel. */
  loading?: boolean;
  /**
   * What the last load for this school found. `false` sizes the desktop placeholder as the
   * fallback tile, so a school with no banners never collapses from banner height.
   */
  expectBanners?: boolean;
  /** Fill the parent's height (the desktop board) instead of the banner's proportions. */
  fill?: boolean;
  testID?: string;
}) {
  const styles = useStyles();
  const theme = useTheme();
  const wide = useChromeKind() === "desktop";
  const [index, setIndex] = useState(0);
  const at = banners.length > 0 ? Math.min(index, banners.length - 1) : 0;
  const banner = banners[at];
  const flip = useFlip(loading ? null : (banner?.id ?? "notice"));
  const breathing = useBreathing(loading);

  if (loading) {
    const phoneBanner = !wide && !fill && expectBanners;
    return (
      <Animated.View
        style={[
          styles.skeleton,
          wide && !expectBanners && styles.fallbackWide,
          !wide && !fill && styles.phoneNotice,
          phoneBanner && styles.skeletonBanner,
          fill && styles.fill,
          breathing,
        ]}
        testID={`${testID}-loading`}
        aria-busy
        accessibilityLabel={schoolName}
      >
        {phoneBanner ? (
          <>
            <View style={styles.skeletonImage} />
            <View style={styles.skeletonBand} />
          </>
        ) : null}
      </Animated.View>
    );
  }
  if (!banner) {
    return (
      <Animated.View
        style={[
          styles.fallback,
          wide && styles.fallbackWide,
          !wide && !fill && styles.phoneNotice,
          fill && styles.fill,
          flip,
        ]}
        testID={`${testID}-fallback`}
        aria-label={schoolName}
      >
        <View style={styles.dots}>
          <DotMatrix ink={theme.color.onTile} size={48} />
        </View>
        <View style={styles.art}>
          <SchoolArt ink={theme.color.onTile} size={wide ? 168 : 112} />
        </View>
        <View />
        <Text
          weight="light"
          size={wide ? "x6l" : "x4l"}
          style={[styles.fallbackText, styles.headline]}
        >
          {noWidow(fallbackText)}
        </Text>
      </Animated.View>
    );
  }
  const go = (step: number) =>
    setIndex((at + step + banners.length) % banners.length);
  return (
    <Animated.View
      style={[styles.hero, fill && styles.fill, flip]}
      testID={testID}
      role="region"
      aria-roledescription="carousel"
      aria-label={schoolName}
    >
      <Image
        source={{ uri: banner.imageUrl }}
        style={[
          styles.image,
          !wide && !fill && styles.phoneImage,
          fill && styles.imageFill,
        ]}
        accessibilityLabel={banner.title}
        accessible
        resizeMode="cover"
        testID={`${testID}-image`}
      />
      <View style={styles.controls}>
        {banners.length > 1 ? (
          <PressableTarget
            accessibilityLabel="Previous banner"
            onPress={() => go(-1)}
          >
            <ChevronIcon direction="left" color={theme.color.onTile} />
          </PressableTarget>
        ) : null}
        <Text
          weight="light"
          size="lg"
          numberOfLines={1}
          style={styles.title}
          aria-hidden
        >
          {banner.title}
        </Text>
        {banners.length > 1 ? (
          <>
            <View style={styles.middle}>
              <View style={styles.pager} aria-hidden>
                {banners.map((b, i) => (
                  <View
                    key={b.id}
                    style={[styles.pip, i === at && styles.pipOn]}
                  />
                ))}
              </View>
              <Text
                size="sm"
                style={styles.position}
                testID={`${testID}-position`}
              >
                {`${at + 1} of ${banners.length}`}
              </Text>
            </View>
            <PressableTarget
              accessibilityLabel="Next banner"
              onPress={() => go(1)}
            >
              <ChevronIcon color={theme.color.onTile} />
            </PressableTarget>
          </>
        ) : null}
      </View>
    </Animated.View>
  );
}
